import { ConnectorMode } from '@blocksuite/affine-model';
import {
  type EdgelessToolbarI18nKey,
  edgelessToolbarI18nT,
  EditPropsStore,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import {
  EdgelessToolbarToolMixin,
  SENIOR_TOOL_ART,
} from '@blocksuite/affine-widget-edgeless-toolbar';
import { SignalWatcher } from '@blocksuite/global/lit';
import { computed } from '@preact/signals-core';
import { css, html, LitElement } from 'lit';

import { ConnectorTool } from '../connector-tool';

/**
 * The cord from the first card's port (bottom left of its box) to the second's (top right), as each
 * mode routes a connector. The box stretches with the cards; the stroke keeps its width.
 */
export const CONNECTOR_CORD_PATHS: Record<ConnectorMode, string> = {
  [ConnectorMode.Straight]: 'M0 24L28 0',
  [ConnectorMode.Orthogonal]: 'M0 24H14V0H28',
  [ConnectorMode.Curve]: 'M0 24C14 24 14 0 28 0',
};

const connectorModeI18nKeyMap: Record<ConnectorMode, EdgelessToolbarI18nKey> = {
  [ConnectorMode.Straight]: 'toolbar.connectorStraight',
  [ConnectorMode.Orthogonal]: 'toolbar.connectorElbowed',
  [ConnectorMode.Curve]: 'toolbar.connectorCurve',
};

/**
 * The connector tool in the dock: two cards joined by a cord drawn in the mode and colour the next
 * connector gets.
 */
export class EdgelessConnectorToolButton extends EdgelessToolbarToolMixin(
  SignalWatcher(LitElement)
) {
  static override styles = [
    SENIOR_TOOL_ART,
    css`
      :host,
      .edgeless-connector-button {
        display: block;
        width: 100%;
        height: 100%;
      }

      .connector-node.from {
        left: 10px;
        width: 30px;
        height: 24px;
        border-radius: 5px;
        --y: -4px;
      }
      .connector-node.to {
        right: 10px;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        --y: -26px;
      }
      .connector-port {
        position: absolute;
        top: 50%;
        width: 7px;
        height: 7px;
        margin-top: -3.5px;
        border-radius: 50%;
        background: var(--art-card);
        border: 1.5px solid var(--connector-color);
      }
      .connector-node.from .connector-port {
        right: -4px;
      }
      .connector-node.to .connector-port {
        left: -4px;
      }
      /* The cord's box spans from port to port and follows the cards at their pace. */
      .connector-cord {
        position: absolute;
        left: 40px;
        top: 24px;
        width: 18px;
        height: 24px;
        overflow: visible;
        transition:
          left 0.4s ease,
          top 0.4s ease,
          width 0.4s ease,
          height 0.4s ease;
      }
      .connector-cord path {
        fill: none;
        stroke: var(--connector-color);
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
        vector-effect: non-scaling-stroke;
      }

      .senior-art:hover .connector-node.from,
      .senior-art.open .connector-node.from {
        --x: -6px;
        --y: 2px;
      }
      .senior-art:hover .connector-node.to,
      .senior-art.open .connector-node.to {
        --x: 6px;
        --y: -40px;
      }
      .senior-art:hover .connector-cord,
      .senior-art.open .connector-cord {
        left: 34px;
        top: 10px;
        width: 30px;
        height: 40px;
      }
    `,
  ];

  private readonly _connector$ = computed(() => {
    const { mode, stroke } =
      this.edgeless.std.get(EditPropsStore).lastProps$.value.connector;
    return {
      mode,
      color: this.edgeless.std.get(ThemeProvider).generateColorProperty(stroke),
    };
  });

  override enableActiveBackground = true;

  override type = ConnectorTool;

  private _toggleMenu() {
    if (this.tryDisposePopper()) return;

    const menu = this.createPopper('edgeless-connector-menu', this);
    menu.element.edgeless = this.edgeless;
    menu.element.onChange = (props: Record<string, unknown>) => {
      this.edgeless.std.get(EditPropsStore).recordLastProps('connector', props);
      this.setEdgelessTool(this.type, {
        mode: this._connector$.value.mode,
      });
    };
  }

  override render() {
    const { mode, color } = this._connector$.value;
    const appTheme = this.edgeless.std.get(ThemeProvider).app$.value;

    return html`<edgeless-toolbar-button
      class="edgeless-connector-button"
      .tooltip=${this.popper
        ? ''
        : html`<affine-tooltip-content-with-shortcut
            data-tip="${edgelessToolbarI18nT(connectorModeI18nKeyMap[mode])}"
            data-shortcut="${'C'}"
          ></affine-tooltip-content-with-shortcut>`}
      .tooltipOffset=${5}
      @click=${() => {
        // don't update tool before toggling menu
        this._toggleMenu();
        this.gfx.tool.setTool(ConnectorTool, {
          mode,
        });
      }}
    >
      <div
        class="senior-art ${this.popper ? 'open' : ''}"
        data-app-theme=${appTheme}
        data-connector-mode=${mode}
        style="--connector-color: ${color}"
      >
        <svg
          class="connector-cord"
          viewBox="0 0 28 24"
          preserveAspectRatio="none"
        >
          <path d=${CONNECTOR_CORD_PATHS[mode]} />
        </svg>
        <div class="art-part art-card connector-node from">
          <svg viewBox="0 0 30 24" fill="none">
            <path
              d="M7 9h12M7 15h8"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <i class="connector-port"></i>
        </div>
        <div class="art-part art-card connector-node to">
          <i class="connector-port"></i>
        </div>
      </div>
    </edgeless-toolbar-button>`;
  }
}
